import { Alert, AlertType } from "shared-components/toast";
import type { LogoutWithMicrosoftArgs } from "./logout";
import { logoutLocallyToLogin } from "./logout";

const AUTH_REDIRECT_ERROR_NAME = "AuthRedirectError";
const AUTH_REDIRECT_ERROR_MESSAGE = "Authentication redirect in progress.";

type RedirectToLoginFromAuthFailureArgs = Pick<
  LogoutWithMicrosoftArgs,
  "clearApolloStore" | "instance"
> & {
  redirectPath?: string;
};

let authRecoveryPromise: Promise<void> | null = null;

export class AuthRedirectError extends Error {
  constructor(message = AUTH_REDIRECT_ERROR_MESSAGE) {
    super(message);
    this.name = AUTH_REDIRECT_ERROR_NAME;
  }
}

export function isAuthRedirectError(
  error: unknown,
): error is AuthRedirectError {
  return (
    error instanceof AuthRedirectError ||
    (error instanceof Error && error.name === AUTH_REDIRECT_ERROR_NAME)
  );
}

export function setPreviousLocation(): void {
  if (window.location.pathname === "/login") {
    return;
  }

  sessionStorage.setItem(
    "prevLocation",
    window.location.pathname + window.location.search,
  );
}

async function clearApolloStoreFromClient(): Promise<void> {
  const { client } = await import("cache/cache");
  await client.clearStore();
}

export function redirectToLoginFromAuthFailure({
  clearApolloStore = clearApolloStoreFromClient,
  instance,
  redirectPath,
}: RedirectToLoginFromAuthFailureArgs): never {
  setPreviousLocation();

  if (!authRecoveryPromise) {
    authRecoveryPromise = logoutLocallyToLogin({
      clearApolloStore,
      instance,
      redirectPath,
    })
      .catch(() => {
        Alert({
          type: AlertType.ERROR,
          message: "Failed to redirect to login.",
        });
      })
      .finally(() => {
        authRecoveryPromise = null;
      });
  }

  throw new AuthRedirectError();
}
